/**
 * @file Renders the Console page for the signed-in user's own profile.
 */
import React, { useEffect, useState } from "react";
import { useAppContext } from "@components/AppContext";

import Page from "@components/Page";
import MyProfile from "@components/MyProfile";
import { type AppContext } from "@components/AppContext/types";

const LABEL_TITLE = "My Profile";

/**
 * Renders the signed-in user's profile, with the mobile number and the two-factor
 * authentication settings kept in sync with the app context.
 */
const MyProfilePage = (): JSX.Element => {
  const { identity, updateIdentityMobile, updateIdentityIs2FaEnabled } = useAppContext() as AppContext & {
    updateIdentityMobile?: (mobile: string) => void;
    updateIdentityIs2FaEnabled?: (is2FaEnabled: boolean) => void;
  };

  const givenName = identity?.givenName;
  const familyName = identity?.familyName;
  const email = identity?.email;
  const mobile = identity?.mobile;
  const [ is2FaEnabled, setIs2FaEnabled ] = useState(identity?.is2FaEnabled);

  const fullName = `${givenName || ""} ${familyName || ""}`.trim();
  /**
   * Applies the new two-factor authentication state to the page and to the app context.
   */
  const onSuccessSwitch2Fa = async (shouldEnable: boolean) => {
    setIs2FaEnabled(shouldEnable);
    updateIdentityIs2FaEnabled?.(shouldEnable);
  };
  /**
   * Applies the new mobile number to the app context.
   */
  const onSuccessUpdateMobile = (updatedMobile: string) => {
    updateIdentityMobile?.(updatedMobile);
  };

  useEffect(() => {
    setIs2FaEnabled(identity?.is2FaEnabled);
  }, [ identity?.is2FaEnabled ]);

  return (
    <Page title={LABEL_TITLE}>
      <MyProfile
        userId={identity?.id || ""}
        name={fullName}
        email={email}
        mobile={mobile}
        is2FaEnabled={is2FaEnabled}
        onSuccessUpdateMobile={onSuccessUpdateMobile}
        onSuccessSwitch2Fa={onSuccessSwitch2Fa}
      />
    </Page>
  );
};

export default MyProfilePage;
